import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileLayout from './ProfileLayout';
import { useUserFirstLetter } from '../hooks/useUserFirstLetter';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import styled from 'styled-components';
import http from '../components/http/http';

const Title = styled.h1`
  font-size: 38px !important;
  font-style: normal;
  font-weight: 700;
  line-height: 52px;
  letter-spacing: 0.8px;
`;

const ReportList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 32px;
`;

const ReportItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #ededed;
  font-size: 14px;
  cursor: pointer;
  transition: background .2s, border-color .2s;

  &:hover {
    background: #e6f7f8;
    border-color: #3baab0;
  }

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
`;

const ReportName = styled.span`
  font-weight: 500;
  color: #333;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ReportDate = styled.span`
  font-size: 13px;
  color: #888;
  white-space: nowrap;
  margin-left: 16px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px 0 32px;
`;

const PageButton = styled.button<{disabled?: boolean}>`
  padding: 8px 20px;
  border: 1px solid #ededed;
  border-radius: 6px;
  background: #fff;
  color: ${({ disabled }) => disabled ? '#ccc' : '#3baab0'};
  font-size: 14px;
  font-weight: 600;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: background .2s, border-color .2s;
  &:hover:not(:disabled) {
    background: #e6f7f8;
    border-color: #3baab0;
  }
`;

const PageInfo = styled.span`
  font-size: 14px;
  color: #888;
`;

interface MyReport {
  _id: string;
  userId: string;
  biomarkersCount: number;
  surveyResponsesCount: number;
  templateVersion: string;
  sourceFileName: string;
  createdAt: string;
  reportName?: { en: string; de: string };
}

export default function MyReports() {
  const firstLetter = useUserFirstLetter();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reports, setReports] = useState<MyReport[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await http.get('/api/my-reports', { limit, offset, raw: true });
      if (res?.success && Array.isArray(res.data)) {
        setReports(res.data);
        setTotal(res.pagination?.total || 0);
      } else {
        setReports([]);
        setTotal(0);
      }
    } catch (e) {
      setReports([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [offset]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <ProfileLayout activeTab="myReports" firstLetter={firstLetter}>
      <Title>{t('myReports')}</Title>
      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#888' }}>Loading...</div>
      ) : reports.length === 0 ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#888' }}>
          {t('noReports')}
        </div>
      ) : (
        <>
          <ReportList>
            {reports.map((report) => (
              <ReportItem
                key={report._id}
                onClick={() => navigate(`/my-report?id=${report._id}&templateVer=${report.templateVersion}&fileName=${encodeURIComponent(report.sourceFileName)}&date=${encodeURIComponent(report.createdAt)}`)}
              >
                <ReportName>
                  {report.reportName?.[(i18n.language || 'en').split('-')[0] as 'en' | 'de'] ?? report.sourceFileName}
                </ReportName>
                <ReportDate>{formatDate(report.createdAt)}</ReportDate>
              </ReportItem>
            ))}
          </ReportList>
          {totalPages > 1 && (
            <PaginationWrapper>
              <PageButton
                disabled={currentPage === 1}
                onClick={() => setOffset(Math.max(0, offset - limit))}
              >
                {t('previous')}
              </PageButton>
              <PageInfo>
                {currentPage} {t('of')} {totalPages}
              </PageInfo>
              <PageButton
                disabled={currentPage === totalPages}
                onClick={() => setOffset(offset + limit)}
              >
                {t('next')}
              </PageButton>
            </PaginationWrapper>
          )}
        </>
      )}
    </ProfileLayout>
  );
}
